/**
 * The conversation rail (UC-A1), from `design/new design /legacy/bank_ai_assistant_chat`.
 *
 * ## This is history, not navigation
 *
 * The design puts the employee's own conversations here — grouped Today / This week / Earlier —
 * with "New conversation" at the top and the disclaimer card at the foot. That is a different
 * thing from the application's navigation, and the difference matters: an agent works inside one
 * conversation for a whole call, and what they need at arm's length is the last thing they asked,
 * not a link to a screen they are already on.
 *
 * ## It is absent below `lg`, and that is the design's own instruction
 *
 * `hidden lg:flex` in the source. A 260px rail beside a transcript on a phone leaves neither
 * usable, so on a small screen the conversation list is reached from the header instead. Every
 * breakpoint in this file is transcribed from the design rather than chosen here.
 */

import { useTranslation } from "react-i18next";

import { SideRail, cx, formatTimestamp, type SessionSummary } from "@nab/shared";

export interface ConversationRailProps {
  sessions: SessionSummary[];
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onNew: () => void;
  /** Rendered as the rail's own element on desktop, or inside the mobile drawer. */
  className?: string;
}

/** Today / This week / Earlier, as the design groups them. */
type Bucket = "today" | "week" | "earlier";

const DAY = 24 * 60 * 60 * 1000;

function bucketOf(iso: string): Bucket {
  const when = Date.parse(iso);
  if (Number.isNaN(when)) return "earlier";
  const age = Date.now() - when;
  if (age < DAY) return "today";
  if (age < 7 * DAY) return "week";
  return "earlier";
}

export function ConversationRail({
  sessions,
  activeSessionId,
  onSelect,
  onNew,
  className,
}: ConversationRailProps): JSX.Element {
  const { t } = useTranslation("app");

  const groups: { bucket: Bucket; items: SessionSummary[] }[] = (
    ["today", "week", "earlier"] as Bucket[]
  )
    .map((bucket) => ({
      bucket,
      items: sessions.filter((s) => bucketOf(s.last_activity_at) === bucket),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <SideRail className={className}>
      <div className="p-4">
        <button
          type="button"
          onClick={onNew}
          className="flex h-[44px] w-full items-center justify-center gap-2 rounded-[10px] bg-primary text-label-md font-bold text-onPrimary transition-transform hover:scale-[1.01]"
        >
          <span aria-hidden="true" className="text-[19px] leading-none">
            +
          </span>
          {t("chat.newConversation")}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        {groups.length === 0 ? (
          <p className="px-1 text-body-sm text-textMuted">{t("chat.noConversations")}</p>
        ) : (
          groups.map(({ bucket, items }) => (
            <div key={bucket} className="mb-6">
              <h3 className="mb-2 text-label-sm font-medium tracking-wide text-textMuted">
                {t(`chat.bucket.${bucket}`)}
              </h3>
              <ul className="flex flex-col gap-1">
                {items.map((session) => {
                  const active = session.session_id === activeSessionId;
                  const title =
                    session.title?.trim() ||
                    t("chat.untitledConversation", {
                      when: formatTimestamp(session.created_at, ""),
                    });
                  return (
                    <li key={session.session_id}>
                      <button
                        type="button"
                        title={title}
                        onClick={() => onSelect(session.session_id)}
                        aria-current={active ? "true" : undefined}
                        className={cx(
                          "w-full truncate rounded-[12px] px-4 py-2 text-start text-body-sm transition-colors",
                          active
                            ? "border border-primaryBorder bg-primarySubtle font-bold text-primary"
                            : "font-medium text-textMuted hover:bg-surfaceRaised hover:text-primary",
                        )}
                      >
                        <bdi>{title}</bdi>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>
    </SideRail>
  );
}
